import PropTypes from 'prop-types';
import { AgGridReact } from 'ag-grid-react';
import { useMemo, useState, useEffect } from 'react';
import { Typography, Box, Accordion, AccordionSummary, Stack, Button, Divider, Tooltip, IconButton, Alert } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { ArrowDownward, AddCircleOutline } from '@mui/icons-material';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';
import AgGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';
import EditIconButton from '../../reuseable-components/DefaultButtons/EditIconButton';
import Iconify from '../../reuseable-components/iconify/Iconify';
import InvoiceItemForm from './InvoiceItemForm';
import InvoiceAddProductForm from './InvoiceAddProductForm'
import axiosInstance from '../../utils/axios';
import { objectToFormData } from '../../utils/objectToFormData';

InvoiceItemTable.propTypes = {
  items: PropTypes.array,
  formData: PropTypes.object,
  gridRef: PropTypes.object,
  setItems: PropTypes.func,
}

export default function InvoiceItemTable({ items, formData, setItems, gridRef }) {
  const [openAddProductForm, setOpenAddProductForm] = useState(false);
  const [openItemForm, setOpenItemForm] = useState(false);
  const [fetchingCustomerPricing, setFetchingCustomerPricing] = useState(false);
  const [invoiceItemNode, setInvoiceItemNode] = useState(null);
  const [invoiceProductNode, setInvoiceProductNode] = useState(null);

  const fetchCustomerPricing = () => {
    setFetchingCustomerPricing(true);
    axiosInstance.post(`/invoice/customerPricing`, objectToFormData({
      facilityId: formData.facilityId,
      orderId: formData.orderNumber,
      vendorId: formData.vendorId,
      items: items.map(item => ({ uomType: item.uomType, vendorItemId: item.vendorItemId }))
    }))
      .then(res => {
        setItems(itemsArr => itemsArr.map(item => {
          const matchingData = res.data.find(custPrice => custPrice.uomType === item.uomType && custPrice.vendorItemId === item.vendorItemId);
          return {
            ...item,
            customerPrice: matchingData?.customerPrice,
            needsToBeAdded: matchingData?.needsToBeAdded
          };
        }));
      })
      .catch(() => enqueueSnackbar('Customer pricing fetch failed', { variant: 'error' }))
      .finally(() => setFetchingCustomerPricing(false));
  };

  const columns = useMemo(() => [
    {
      field: 'description',
      minWidth: 150,
      flex: 1
    },
    { field: 'vendorItemId' },
    {
      field: 'uomType',
      headerName: 'UOM',
    },
    {
      field: 'price',
      cellRenderer: (params) => params.value ? params.value.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : "-"
    },
    {
      field: 'customerPrice',
      cellRenderer: (params) => params.value ? params.value.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : "-"
    },
    {
      field: 'tax',
      cellRenderer: (params) => params.data?.taxAmount ? params.data.taxAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : "-"
    },

    ...(formData.billed ? [
      {
        field: 'customerTaxAmount',
        headerName: 'Customer Tax',
        cellRenderer: (params) => params.data?.customerTaxAmount ? params.data.customerTaxAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : "-"
      }

    ] : []),
    {
      field: 'quantity',
      headerName: 'QTY',
    },
    {
      field: 'total',
      cellRenderer: (params) => {
        const price = params.data?.price ?? 0;
        const quantity = params.data?.quantity ?? 0;
        const taxAmount = params.data?.taxAmount ?? 0;
        const total = (price * quantity) + taxAmount;
        return (
          <Tooltip title={`${price.toLocaleString('en-US', { style: 'currency', currency: 'USD' })} * 
      ${quantity} + ${taxAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`}>
            <Box>
              {total.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
            </Box>
          </Tooltip>
        );
      }
    },

    {
      field: 'actions',
      minwidth: 150,
      cellRenderer: (params) => <Stack direction="row" spacing={1}>
        <EditIconButton onClick={() => { setInvoiceItemNode(params.node); setOpenItemForm(true); }} />
        <IconButton color="error" disableRipple sx={{ px: 0 }} onClick={() => {
          setItems(files => {
            params.api.applyTransaction({ remove: [params.data] });
            return [...files.filter(file => file.id !== params.data.id)];
          })
        }}>
          <DeleteOutline />
        </IconButton>
        {params.data.needsToBeAdded && (
          <Tooltip title="Add to catalog" arrow>
            <IconButton disableRipple sx={{ px: 0 }} onClick={() => { setInvoiceProductNode(params.node); setOpenAddProductForm(true); }}>
              <AddCircleOutline />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    },
  ], []);

  return <Box sx={{ border: "solid thin rgb(43 45 47 / 24%)" }}>
    <Accordion>
      <AccordionSummary expandIcon={<ArrowDownward />}>
        <Typography component="span">Items</Typography>
      </AccordionSummary>
      <Box sx={openAddProductForm ? { m: 3 } : { display: "none" }}>
        <InvoiceAddProductForm open={openAddProductForm} invoiceProductNode={invoiceProductNode} setInvoiceItems={setItems} vendorID={formData.vendorId}
          close={() => { setInvoiceProductNode(null); setOpenAddProductForm(false); }} />
        <Divider />
      </Box>
      <Box sx={openItemForm ? { m: 3 } : { display: "none" }}>
        <InvoiceItemForm gridApi={gridRef?.current} open={openItemForm} invoiceItemNode={invoiceItemNode} setInvoiceItems={setItems} setInvoiceItemNode={setInvoiceItemNode} formData={formData}
          close={() => { setInvoiceItemNode(null); setOpenItemForm(false); }} />
        <Divider />
      </Box>
      <Stack sx={{ mx: 2, mb: 2 }} spacing={1}>
        <AGGridQuickFilterWrapper gridRef={gridRef}>
          <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => { setInvoiceItemNode(null); setInvoiceProductNode(null); setOpenItemForm(true); }}
            color="primary" size="small">
            <Typography>Add Item</Typography>
          </Button>
          <LoadingButton loading={fetchingCustomerPricing} color="purple" size="medium" variant="contained" onClick={fetchCustomerPricing}>
            Fetch customer pricing
          </LoadingButton>
        </AGGridQuickFilterWrapper>
        <Box sx={{ height: openItemForm ? "250px" : "350px" }}>
          <AgGridReact ref={gridRef} {...AgGridDefaults} columnDefs={columns} rowData={items} />
        </Box>
      </Stack>
    </Accordion>
  </Box>;
}
